import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { addExchangeRate, getCurrencies, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { FaArrowRight } from 'react-icons/fa';

const AddExchangeRate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { currencies, isLoading, error, success } = useSelector((state) => state.auth);
  const [form, setForm] = useState({
    currency_id: '',
    date: '',
    rate: ''
  });
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('exchangeRates.add');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getCurrencies({ page: 1, per_page: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/exchange-rates');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'rate' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      currency_id: parseInt(form.currency_id),
      date: form.date,
      rate: parseFloat(form.rate)
    };
    await dispatch(addExchangeRate(submitData));
  };

  // Detect sidebar state from body class (set by Layout)

  // Always expand form to 100% width when sidebar is open or closed
  const isWide = document.body.classList.contains('with-sidenav') || document.body.classList.contains('full-width');

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      direction: dir
    }}>
      <div style={{ marginBottom: '10px', width: '100%', maxWidth: isWide ? '100%' : '750px' }}>
        <button
          onClick={() => navigate('/exchange-rates')}
          style={{
            background: '#666',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
      </div>
      <h1 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '14px',
        textAlign
      }}>
        {t('exchangeRates.add', { defaultValue: 'Add Exchange Rate' })}
      </h1>

      <form onSubmit={handleSubmit} style={{
        backgroundColor: '#1b3350',
        border: '1px solid #333',
        borderRadius: '12px',
        padding: '32px',
        width: '100%',
        maxWidth: isWide ? '100%' : '750px',
        direction: dir
      }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('exchangeRates.form.currency', { defaultValue: 'Currency' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <select
            name="currency_id"
            value={form.currency_id}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              direction: dir,
              textAlign
            }}
          >
            <option value="">{t('exchangeRates.form.selectCurrency', { defaultValue: 'Select Currency' })}</option>
            {(currencies || []).map((currency) => {
              const currencyName = isRTL ? (currency.name_ar || currency.name) : (currency.name_en || currency.name);
              return (
                <option key={currency.id} value={currency.id}>
                  {currency.code} - {currencyName}
                </option>
              );
            })}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('exchangeRates.form.date', { defaultValue: 'Date' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="date"
            name="date"
            value={form.date}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              direction: dir
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('exchangeRates.form.rate', { defaultValue: 'Rate' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="number"

            name="rate"
            value={form.rate}
            onChange={handleChange}
            required
            placeholder={t('exchangeRates.form.ratePlaceholder', { defaultValue: 'Enter exchange rate' })}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              boxSizing: 'border-box',
              direction: dir,
              textAlign
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginTop: '30px',
          flexDirection: isRTL ? 'row' : 'row-reverse'
        }}>
          <button
            type="button"
            onClick={() => navigate('/exchange-rates')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddExchangeRate;

